// SOURCE: https://github.com/j-mendez/next-intercom
/*
The MIT LICENSE (MIT)
Copyright (c) A11yWatch LLC
Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

import type { IIntercomProps } from "./types";
import { loadScript } from "./load-script";
import { hasIntercom } from "./has-intercom";

let APP_ID = "";
const widgetCdn = "https://widget.intercom.io/widget/";

function setAppId(id: string): void {
  if (id) {
    APP_ID = id;
  }
}

function updateIntercom(event = "update", settings: any = null): void {
  hasIntercom() && window.Intercom(event, settings);
}

function trackEvent(type: string, metadata: any = null): void {
  hasIntercom() && window.Intercom("trackEvent", type, metadata);
}

async function createIntercomSSR(appId: string = APP_ID): Promise<string> {
  let script = "";
  try {
    const dataSource = await fetch(`${widgetCdn}${appId}`);
    const scriptBody = await dataSource.text();

    script = `<script type="text/javascript" async="true">${scriptBody}</script>`;
  } catch (error) {
    console.error(error);
  }

  return script;
}

function initIntercomWindow({ appId = APP_ID, ...otherProps }): void {
  const intercomProps = Object.assign(
    {},
    {
      api_base: "https://api-iam.intercom.io",
      app_id: appId,
    },
    otherProps,
  );

  if (hasIntercom()) {
    if (window.intercomSettings && !window.intercomSettings.app_id) {
      window.intercomSettings = intercomProps;
    }

    window.Intercom("boot", intercomProps);
  }
}

function loadIntercom({ appId = APP_ID, delay = 0, initWindow = true, ...extra }: IIntercomProps) {
  setAppId(appId);

  if (extra.ssr) {
    return createIntercomSSR(appId);
  } else if (typeof window !== "undefined") {
    if (typeof window.Intercom === "function") {
      updateIntercom("reattach_activator");
      updateIntercom("update", window.intercomSettings);
    } else if (typeof document !== "undefined") {
      const intercomBase: any = function (...args: any) {
        intercomBase.c(args);
      };

      intercomBase.q = [];
      intercomBase.c = function (args) {
        intercomBase.q.push(args);
      };

      window.Intercom = intercomBase;

      const { callBack, scriptType, email, name, ...stripedProps } = extra || {};

      const baseLoadProps = {
        src: `${widgetCdn}${appId}`,
        callBack,
        initWindow,
        initIntercomWindow,
        scriptType,
      };

      const updateProps = Object.assign(
        {},
        {
          email: email || null,
          appId,
          name,
        },
        stripedProps,
      );

      const onLoadScript = () => loadScript(baseLoadProps, updateProps);

      delay ? setTimeout(onLoadScript, delay) : onLoadScript();
    } else {
      console.warn("intercom failed to load");
    }
  }
}

function showIntercomWindow(): void {
  hasIntercom() && window.Intercom("show");
}

function shutdownIntercom(): void {
  if (hasIntercom()) {
    window.Intercom("shutdown");
    delete window.Intercom;
    delete window.intercomSettings;
  }
}

export {
  updateIntercom,
  trackEvent,
  createIntercomSSR,
  setAppId,
  loadIntercom,
  initIntercomWindow,
  showIntercomWindow,
  shutdownIntercom,
  APP_ID,
};
